import { useEffect, useState } from 'react';

import { useTrackEventAmplitude } from '../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../design-system/components/form/button/Button';
import { IconCross } from '../../../../design-system/components/icons/Cross';
import { Typography } from '../../../../design-system/components/typography/Typography';
import { useBreakPoint } from '../../../../domains/Layout/BreakPoint/BreakPoint';
import { Destination } from '../../../../resources/destination/domain/types/destination';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';

import style from './AbTripDesignerBanner.module.css';

type Props = {
    destination: PickDomain<Destination, 'id' | 'slug'>;
    source: 'facet' | 'destination' | 'list-itinerary';
};

function IconIA() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 21 20"
            fill="none"
        >
            <path
                d="M19.2901 9.09458C15.1959 7.3545 13.2569 5.49228 11.521 1.47437C10.8617 -0.158729 10.3064 -0.705936 9.51492 1.24432C7.78372 5.3995 5.71723 7.38006 1.64847 9.09458C1.23952 9.28298 0.504722 9.61149 0.5 9.99965C0.504722 10.3888 1.23574 10.7163 1.64847 10.9047C5.7125 12.6145 7.78372 14.5998 9.51492 18.755C10.3158 20.7403 10.8731 20.1164 11.521 18.5249C13.2569 14.5089 15.1959 12.6458 19.2901 10.9047C19.7189 10.7002 20.4282 10.4285 20.5 10.0205V9.97409C20.4282 9.56983 19.7246 9.29434 19.2901 9.08984V9.09458Z"
                fill="#003526"
            />
        </svg>
    );
}

export function AbTripDesignerBanner({ destination, source }: Props) {
    const tripDesignerUrl = `https://trip-designer.evaneos.fr/onboarding/destination?destinationId=${destination.id}&utm_source=evaneos&utm_medium=banner&utm_campaign=page-${source}`;
    const { trackEvent } = useTrackEventAmplitude();
    const [isBannerClosed, setIsBannerClosed] = useState<boolean>(() => {
        if (typeof window === 'undefined') return false;
        return localStorage.getItem('ab-trip-designer-banner-closed') === 'true';
    });
    const [isScrollTriggered, setIsScrollTriggered] = useState(false);
    const { isDesktop } = useBreakPoint();

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const storedValue = localStorage.getItem('ab-trip-designer-banner-closed') === 'true';
            if (storedValue !== isBannerClosed) {
                setIsBannerClosed(storedValue);
            }
        }
    }, [isBannerClosed]);

    useEffect(() => {
        // Don't set up observer if banner is already closed
        if (isBannerClosed) {
            return;
        }

        // Desktop (≥992px): target 5th card (index 4) for 2nd row with 4 cards/row
        // Mobile (<992px): target 4th card (index 3)
        const targetIndex = isDesktop() ? 4 : 3;
        let targetElement: Element | null = document.querySelector(
            `[data-itinerary-index="${targetIndex}"]`,
        );

        // Fallback: if target card doesn't exist, observe the "See all" button instead
        if (!targetElement) {
            targetElement = document.querySelector('[data-see-all-itineraries-button]');
        }

        if (!targetElement) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setIsScrollTriggered(true);
                    trackEvent((ampli, defaultProperties) => {
                        ampli.pathfinderTripDesignerBannerDisplayed({
                            ...defaultProperties,
                            destination_id: destination.id,
                            destination_slug: destination.slug,
                        });
                    });
                    observer.disconnect();
                }
            },
            { threshold: 0.5 },
        );

        observer.observe(targetElement);

        return () => observer.disconnect();
    }, [destination.id, destination.slug, trackEvent, isBannerClosed, isDesktop]);

    const handleRedirectToTripBuilder = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli
                .pathfinderTripBuilderBannerClicked({
                    ...defaultProperties,
                    destination_id: destination.id,
                    destination_slug: destination.slug,
                })
                .promise.then(() => {
                    window.location.href = tripDesignerUrl;
                })
                .catch(() => {
                    window.location.href = tripDesignerUrl;
                });
        });
    };

    const handleCloseBanner = () => {
        setIsBannerClosed(true);
        if (typeof window !== 'undefined') {
            localStorage.setItem('ab-trip-designer-banner-closed', 'true');
        }
        trackEvent((ampli, defaultProperties) => {
            ampli.pathfinderTripDesignerBannerClosed({
                ...defaultProperties,
                destination_id: destination.id,
                destination_slug: destination.slug,
            });
        });
    };

    // Don't show if already closed globally or not yet scroll-triggered
    if (isBannerClosed || !isScrollTriggered) {
        return null;
    }

    return (
        <div className={style.container}>
            <div className={style.banner}>
                <button
                    type="button"
                    className={style.closeButton}
                    onClick={handleCloseBanner}
                    aria-label="Fermer"
                >
                    <IconCross className={style.closeIcon} />
                </button>
                <div className={style.content}>
                    <Typography scale="primary-body-bold" className={style.title}>
                        Concevez votre prochain voyage,
                        <br />
                        assisté par l&apos;IA avec notre Trip Designer
                    </Typography>
                    <Button
                        onClick={handleRedirectToTripBuilder}
                        iconPosition="left"
                        Icon={IconIA}
                        className={style.ctaButton}
                    >
                        Générer mon itinéraire
                    </Button>
                </div>
            </div>
        </div>
    );
}
